import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { mockPlants } from '../../data/mockData';
import { SectionBadge } from '../BrandAssets';

const { FiPlus, FiDroplet, FiSun, FiClock, FiSearch } = FiIcons;

export default function LibraryView() {
  const [filter, setFilter] = useState('All');
  const filters = ['All', 'Herb', 'Vegetable', 'Houseplant'];
  
  const filteredPlants = filter === 'All' 
    ? mockPlants 
    : mockPlants.filter(p => p.type === filter);

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-8 pb-24 md:pb-6">
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-6">
        <div>
          <SectionBadge label="Flora Archive" />
          <h2 className="text-3xl font-bold text-clay mt-2 tracking-tight">Biological Inventory</h2>
          <p className="text-sm text-clay-muted">Comprehensive database of your domestic urban ecosystem.</p>
        </div>
        <div className="flex w-full lg:w-auto gap-3">
          <div className="relative flex-1 lg:w-64">
            <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 -translate-y-1/2 text-clay-dark" />
            <input 
              type="text" 
              placeholder="Query species..." 
              className="w-full bg-garden-dark border border-garden-base rounded-xl py-2.5 pl-10 pr-4 text-xs text-clay focus:outline-none focus:border-lime/50"
            />
          </div>
          <button className="flex items-center gap-2 bg-lime text-garden-darkest px-5 py-2.5 rounded-xl text-sm font-extrabold hover:scale-105 active:scale-95 transition-all shadow-[0_0_20px_rgba(163,230,53,0.3)]">
            <SafeIcon icon={FiPlus} className="text-lg" />
            INIT NEW
          </button>
        </div>
      </div>

      <div className="flex gap-2 overflow-x-auto pb-4 scrollbar-hide">
        {filters.map(f => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`px-5 py-2 rounded-full text-xs font-bold whitespace-nowrap transition-all border ${
              filter === f 
                ? 'bg-lime text-garden-darkest border-lime shadow-[0_0_15px_rgba(163,230,53,0.2)]' 
                : 'bg-garden-dark text-clay-muted border-garden-base hover:text-clay hover:border-clay-muted'
            }`}
          >
            {f.toUpperCase()}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">
        <AnimatePresence mode="popLayout">
          {filteredPlants.map((plant, i) => (
            <motion.div
              layout
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{ delay: i * 0.05 }}
              key={plant.id}
              className="group glass-panel rounded-2xl overflow-hidden border border-garden-base hover:border-lime/40 transition-all duration-500 flex flex-col h-[420px]"
            >
              <div className="h-56 relative overflow-hidden shrink-0">
                <img 
                  src={plant.image} 
                  alt={plant.name} 
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700 opacity-90 brightness-[0.85]" 
                />
                <div className="absolute inset-0 bg-gradient-to-t from-garden-darkest via-transparent to-transparent opacity-60"></div>
                
                <div className="absolute top-4 left-4 bg-garden-darkest/90 backdrop-blur px-2.5 py-1 rounded-lg text-[10px] font-mono text-lime border border-lime/30">
                  REF: {plant.id.toUpperCase()}
                </div>
                
                <div className="absolute bottom-4 left-4 right-4">
                  <h3 className="text-xl font-black text-clay truncate drop-shadow-lg">{plant.name}</h3>
                  <p className="text-xs font-mono text-lime/80 mt-0.5">{plant.type}</p>
                </div>
              </div>

              <div className="p-6 flex-1 flex flex-col justify-between bg-garden-dark/30">
                <div className="grid grid-cols-2 gap-y-4 gap-x-6">
                  <div className="space-y-1">
                    <p className="text-[9px] text-clay-dark font-mono uppercase tracking-widest">Hydration</p>
                    <div className="flex items-center gap-3">
                      <div className="flex-1 h-1.5 bg-garden-darkest rounded-full overflow-hidden border border-garden-base">
                        <motion.div 
                          initial={{ width: 0 }}
                          animate={{ width: `${plant.hydration}%` }}
                          className={`h-full ${plant.hydration < 50 ? 'bg-red-400' : 'bg-lime shadow-[0_0_8px_rgba(163,230,53,0.5)]'}`}
                        />
                      </div>
                      <span className="text-xs font-mono text-clay">{plant.hydration}%</span>
                    </div>
                  </div>
                  <div className="space-y-1">
                    <p className="text-[9px] text-clay-dark font-mono uppercase tracking-widest">System Status</p>
                    <p className="text-xs font-bold text-lime flex items-center gap-2">
                       <span className="w-1.5 h-1.5 rounded-full bg-lime animate-pulse"></span>
                       {plant.status}
                    </p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-[9px] text-clay-dark font-mono uppercase tracking-widest">Rhythm</p>
                    <p className="text-xs text-clay-muted flex items-center gap-2">
                      <SafeIcon icon={FiDroplet} className="text-blue-400" /> {plant.waterFreq}
                    </p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-[9px] text-clay-dark font-mono uppercase tracking-widest">Exposure</p>
                    <p className="text-xs text-clay-muted flex items-center gap-2">
                      <SafeIcon icon={FiSun} className="text-yellow-400" /> {plant.sun}
                    </p>
                  </div>
                </div>
                
                <button className="w-full mt-6 py-2 border border-garden-base rounded-xl text-[10px] font-mono text-clay-dark uppercase hover:bg-garden-base hover:text-lime hover:border-lime/30 transition-all">
                  Access Sensor Logs
                </button>
              </div>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
    </motion.div>
  );
}